import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "../styles/shared-ui.css";
import "../styles/paei-analysis.css";
import SearchableDropdown from "../components/ui/SearchableDropdown";
import { getProcessesPaginated } from "../services/processService";
import {
  getPaeiGoals,
  getPaeiHeuristics,
  getPaeiCoverage,
  runPaeiAnalysis,
  DEFAULT_GOAL_PRESETS,
} from "../services/paeiAnalysisService";
import { StatusBar } from "../components/paei/paeiPrimitives";
import PaeiOverviewTab from "../components/paei/PaeiOverviewTab";
import PaeiReassignmentsTab from "../components/paei/PaeiReassignmentsTab";
import PaeiRedesignTab from "../components/paei/PaeiRedesignTab";
import PaeiRequirementsTab from "../components/paei/PaeiRequirementsTab";
import PaeiShiftReferenceTab from "../components/paei/PaeiShiftReferenceTab";
import PaeiJobPoolTab, { buildJobPool } from "../components/paei/PaeiJobPoolTab";
import PaeiCapiTab from "../components/paei/PaeiCapiTab";
import { toast } from "../utils/toast";

// CAPI & PAEI Analysis.
//
// Read-only by construction: this screen computes and shows, it writes nothing.
// Editing a PAEI or CAPI profile stays in CMS's own /paei and /capi CRUD.
//
// Three things decide whether anything else on the page means what it looks like,
// so they are read before any number is rendered: `analysable`, the pool coverage,
// and the currency block. The reading-status and ordering-check bars sit above the
// tabs for the same reason — a run where the model was unreachable is a keyword
// run wearing the same UI.

const READING_TONES = {
  llm: { tone: "ok", tag: "read by model" },
  mixed: { tone: "warn", tag: "mixed" },
  fallback: { tone: "danger", tag: "keyword fallback" },
  disabled: { tone: "warn", tag: "keywords only" },
};

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "capi", label: "CAPI analysis" },
  { key: "reassignments", label: "Reassignments" },
  { key: "redesign", label: "Redesign" },
  { key: "requirements", label: "Requirements" },
  { key: "reference", label: "Shift reference" },
  { key: "pool", label: "Job pool" },
];

function bandCopy(band, percent) {
  const pct = Number.isFinite(Number(percent)) ? `${Number(percent).toFixed(0)}%` : "—";
  if (band === "thin") {
    return {
      tone: "danger",
      tag: "thin pool",
      text: `Only ${pct} of the jobs on this process have a PAEI profile available to you. The optimizer had few people to choose from, so treat the recommendation as a direction, not an answer.`,
    };
  }
  if (band === "partial") {
    return {
      tone: "warn",
      tag: "partial pool",
      text: `${pct} of the jobs on this process have a PAEI profile available to you. The analysis will run, but a wider pool would likely change the recommendation.`,
    };
  }
  return {
    tone: "ok",
    tag: "pool ready",
    text: `${pct} of the jobs on this process have a PAEI profile available to you.`,
  };
}

function CapiPaeiAnalysisPage() {
  /* -------------------------------- controls ------------------------------ */
  const [processes, setProcesses] = useState([]);
  const [processesLoading, setProcessesLoading] = useState(true);
  const [processQuery, setProcessQuery] = useState("");
  const [selectedProcess, setSelectedProcess] = useState(null);

  // Seeded with the presets the agent accepts, so a goal is always selectable
  // even before (or without) the /goals call. The chip value IS the goal sent to
  // the agent — it rejects anything outside this set.
  const [goals, setGoals] = useState(DEFAULT_GOAL_PRESETS);
  const [selectedGoal, setSelectedGoal] = useState(DEFAULT_GOAL_PRESETS[0].key);
  const [useLlm, setUseLlm] = useState(true);
  const [refreshReading, setRefreshReading] = useState(false);

  const [reference, setReference] = useState([]);
  const [referenceError, setReferenceError] = useState(null);

  /* -------------------------------- coverage ------------------------------ */
  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [coverageError, setCoverageError] = useState(null);

  /* ---------------------------------- run --------------------------------- */
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  // The job's own stage_detail, rendered verbatim. A run that sits silent for 40
  // seconds reads as a hang even while it is working — that is what gets
  // reported as a timeout.
  const [stageDetail, setStageDetail] = useState(null);
  const [runError, setRunError] = useState(null);
  const [result, setResult] = useState(null);

  const [activeTab, setActiveTab] = useState("overview");
  const tabRefs = useRef([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: list } = await getProcessesPaginated({ limit: 500 });
        if (!cancelled) setProcesses(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) toast.error(e?.message || "Failed to load processes");
      } finally {
        if (!cancelled) setProcessesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Goals and the nine-row shift reference are static per deployment — fetch once.
  useEffect(() => {
    let cancelled = false;
    getPaeiGoals().then((g) => {
      if (cancelled || g.length === 0) return;
      setGoals(g);
      setSelectedGoal((prev) => (g.some((x) => x.key === prev) ? prev : g[0].key));
    });

    getPaeiHeuristics()
      .then((h) => !cancelled && setReference(h))
      .catch((e) => !cancelled && setReferenceError(e?.message || "Shift reference unavailable"));

    return () => { cancelled = true; };
  }, []);

  // A 90-second first run must not look hung.
  useEffect(() => {
    if (!running) return undefined;
    setElapsed(0);
    const started = Date.now();
    const id = setInterval(() => setElapsed(Math.round((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(id);
  }, [running]);

  // Coverage is a pre-flight: it fires on process select so the warning arrives
  // before anyone waits out a thin answer. It never gates the Run button.
  const loadCoverage = useCallback(async (processId) => {
    setCoverageLoading(true);
    setCoverage(null);
    setCoverageError(null);
    try {
      const c = await getPaeiCoverage(processId);
      setCoverage(c);
    } catch (e) {
      setCoverageError(e?.message || "Coverage could not be checked");
    } finally {
      setCoverageLoading(false);
    }
  }, []);

  const processOptions = useMemo(
    () =>
      processes.map((p) => ({
        label: `${p.process_name}${p.process_code ? ` (${p.process_code})` : ""}`,
        value: p.process_id,
        process: p,
      })),
    [processes],
  );

  const handlePickProcess = (label, option) => {
    if (!option) return;
    setSelectedProcess(option.process);
    setProcessQuery(label);
    setResult(null);
    setRunError(null);
    setActiveTab("overview");
    loadCoverage(option.process.process_id);
  };

  const handleRun = async () => {
    if (!selectedProcess || running) return;
    setRunning(true);
    setRunError(null);
    setResult(null);
    setStageDetail(null);
    try {
      const res = await runPaeiAnalysis(selectedProcess.process_id, {
        goal: selectedGoal || undefined,
        useLlm,
        refreshReading: refreshReading || undefined,
        onStage: setStageDetail,
      });
      setResult(res);
      setRefreshReading(false);
      setActiveTab("overview");
    } catch (e) {
      setRunError(e?.message || "The analysis failed to run");
    } finally {
      setRunning(false);
    }
  };

  /* -------------------------------- derived ------------------------------- */

  const analysable = result ? result.analysable !== false : true;
  const poolCoverage = result?.process?.pool_coverage || null;
  const currency = result?.process?.currency || null;
  const reading = result?.reading || null;
  const ordering = result?.ordering_check || null;

  const currencyWarning =
    currency && (currency.applied === false || (currency.unknown_codes || []).length > 0);

  const counts = useMemo(
    () => ({
      reassignments: (result?.restaffing || []).length,
      redesign: (result?.redesign?.heuristics || []).length,
      pool: result ? buildJobPool(result).length : 0,
      // The service's own count of jobs on this process carrying a CAPI profile
      // — not a length this page derives.
      capi: result?.capi?.coverage?.jobs_with_capi ?? null,
    }),
    [result],
  );

  // Coverage banner: prefer the run's own numbers once we have them, since the
  // pool the optimizer actually reached is the one worth warning about.
  const bandSource = result ?
    { band: result.band, percent: poolCoverage?.percent, note: poolCoverage?.note }
  : coverage ?
    { band: coverage.band, percent: coverage.pool_coverage?.percent ?? coverage.percent, note: coverage.pool_coverage?.note ?? coverage.note }
  : null;

  const bandInfo = bandSource?.band ? bandCopy(bandSource.band, bandSource.percent) : null;

  // The coverage call says what this run will cost in time. Say it on the button
  // rather than letting the user discover it after pressing it.
  const readingCost = coverage?.reading || null;
  const runLabel =
    running ? "Running…"
    : readingCost && readingCost.fully_cached === false ? "Run analysis (first run, ~1 min)"
    : "Run analysis";

  const selectedGoalMeta = goals.find((g) => g.key === selectedGoal) || null;

  const handleTabKeyDown = (e, index) => {
    const last = TABS.length - 1;
    let next = null;
    if (e.key === "ArrowRight") next = index === last ? 0 : index + 1;
    else if (e.key === "ArrowLeft") next = index === 0 ? last : index - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = last;
    if (next == null) return;
    e.preventDefault();
    setActiveTab(TABS[next].key);
    tabRefs.current[next]?.focus();
  };

  const readingTone = reading ? READING_TONES[reading.status] || READING_TONES.mixed : null;

  return (
    <div className="page-container pa-page">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">CAPI &amp; PAEI Analysis</h2>
            <p className="page-subtitle">
              Read what each step of a process actually demands, then see who should be holding it —
              as declared, restaffed, and after the demand shifts. Nothing here writes to a staffing
              record.
            </p>
          </div>
        </div>
      </div>

      <div className="page-content pa-content">
        {/* -------------------------- controls -------------------------- */}
        <section className="pa-card pa-controls">
          <div className="pa-control-row">
            <div className="pa-control">
              <label className="pa-control-label" htmlFor="pa-process">Process</label>
              <SearchableDropdown
                id="pa-process"
                options={processOptions}
                value={processQuery}
                onChange={(text, option) => {
                  setProcessQuery(text);
                  if (option) handlePickProcess(text, option);
                }}
                disabled={processesLoading}
                placeholder={processesLoading ? "Loading processes…" : "Search by process name or code…"}
              />
            </div>

            <div className="pa-control">
              <span className="pa-control-label">Goal</span>
              <div className="pa-chips" role="group" aria-label="Goal preset">
                {goals.map((g) => (
                  <button
                    key={g.key}
                    type="button"
                    className="pa-goal-chip"
                    aria-pressed={selectedGoal === g.key}
                    title={g.description || ""}
                    onClick={() => setSelectedGoal(g.key)}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
              {selectedGoalMeta?.description && (
                <span className="pa-muted">{selectedGoalMeta.description}</span>
              )}
            </div>

            <div className="pa-control">
              <span className="pa-control-label">Read descriptions with</span>
              <div className="pa-seg" role="group" aria-label="Reading mode">
                <button type="button" aria-pressed={useLlm} onClick={() => setUseLlm(true)}>
                  Model
                </button>
                <button type="button" aria-pressed={!useLlm} onClick={() => setUseLlm(false)}>
                  Keywords
                </button>
              </div>
              {!useLlm && (
                <span className="pa-muted">
                  The keyword fallback misreads roughly one step in four.
                </span>
              )}
            </div>

            <div className="pa-control pa-run-control">
              <label className="pa-checkbox">
                <input
                  type="checkbox"
                  checked={refreshReading}
                  onChange={(e) => setRefreshReading(e.target.checked)}
                />
                Re-read descriptions
              </label>
              <button
                type="button"
                className="primary-btn"
                disabled={!selectedProcess || running}
                onClick={handleRun}
              >
                {runLabel}
              </button>
              {readingCost?.estimate && !running && (
                <span className="pa-muted">{readingCost.estimate}</span>
              )}
            </div>
          </div>

          {running && (
            <div className="pa-run-status" role="status" aria-live="polite">
              <span className="pa-spinner" aria-hidden="true" />
              <div>
                <div>
                  {/* The server's own words for where it is, never a guess of ours. */}
                  {stageDetail || "Starting the analysis…"} <b>{elapsed}s</b>
                </div>
                <div className="pa-muted">
                  A step is read with the model only when its text has changed, so a cold run takes
                  around a minute and every later run of the same process comes back in seconds.
                </div>
              </div>
            </div>
          )}

          {runError && <div className="pa-banner danger">{runError}</div>}

          {coverageLoading && !result && (
            <div className="pa-muted" style={{ marginTop: 12 }}>Checking pool coverage…</div>
          )}
          {coverageError && !result && (
            <div className="pa-banner warn">
              Coverage could not be checked ({coverageError}). The analysis can still be run.
            </div>
          )}
        </section>

        {/* --------------------- coverage / status bars ------------------- */}
        {bandInfo && (
          <StatusBar tone={bandInfo.tone} tag={bandInfo.tag}>
            <p>{bandInfo.text}</p>
            {/* Rendered verbatim, always: what the pool contains depends on who is
                asking, so the same process can reach a different recommendation
                for an ADMIN than for a SUPER_ADMIN. */}
            {bandSource.note && <p className="pa-verbatim">{bandSource.note}</p>}
          </StatusBar>
        )}

        {reading && (
          <StatusBar
            tone={readingTone.tone}
            tag={readingTone.tag}
            meta={[
              reading.provider ? `${reading.provider}${reading.model ? ` · ${reading.model}` : ""}` : null,
              `${reading.samples ?? 1} sample${(reading.samples ?? 1) === 1 ? "" : "s"}`,
              `spread ${reading.max_spread ?? 0}`,
              `${(reading.low_confidence_task_ids || []).length} low-confidence`,
            ]}
          >
            <p className="pa-verbatim">{reading.note}</p>
            {reading.reproducibility && (
              <p className="pa-verbatim pa-muted">{reading.reproducibility}</p>
            )}
            {reading.error && (
              <p className="pa-verbatim">
                {reading.error.kind}: {reading.error.message}
              </p>
            )}
          </StatusBar>
        )}

        {ordering && (
          <StatusBar
            tone={ordering.differs ? "warn" : "ok"}
            tag={ordering.differs ? "order matters" : "order-independent"}
          >
            <p className="pa-verbatim">{ordering.note}</p>
            {ordering.differs && (
              <div className="pa-order-detail">
                {ordering.lost_by_restaffing_first?.length > 0 && (
                  <span>
                    Lost if staffed first: <b>{ordering.lost_by_restaffing_first.join(", ")}</b>
                  </span>
                )}
                {ordering.gained_by_restaffing_first?.length > 0 && (
                  <span>
                    Gained if staffed first: <b>{ordering.gained_by_restaffing_first.join(", ")}</b>
                  </span>
                )}
              </div>
            )}
          </StatusBar>
        )}

        {currencyWarning && (
          <StatusBar tone="warn" tag="currency">
            <p className="pa-verbatim">{currency.note}</p>
            {(currency.unknown_codes || []).length > 0 && (
              <p>
                Jobs priced in <b>{currency.unknown_codes.join(", ")}</b> could not be converted —
                every cost involving them is wrong, not merely approximate.
              </p>
            )}
          </StatusBar>
        )}

        {/* ----------------------------- tabs ---------------------------- */}
        {result && (
          <>
            <div className="pa-result-head">
              <div>
                <h3>{result.process?.process_name}</h3>
                <div className="pa-muted">
                  {result.process?.process_code} · v{result.process?.process_version}
                  {result.goal?.label ? ` · goal: ${result.goal.label}` : ""}
                  {result.cached ? " · from cache" : ""}
                  {result.bpmn_source ? ` · BPMN ${result.bpmn_source}` : ""}
                </div>
              </div>
              {currency?.reporting && (
                <span className="pa-tag idle">all money in {currency.reporting}</span>
              )}
            </div>

            {!analysable && (
              <div className="pa-banner danger">
                <strong>This process cannot be analysed as staffed.</strong>{" "}
                {result.reason || "No slot on it has a PAEI profile available to you."} The derived
                requirements are still shown under <b>Requirements</b> — they hold on their own, and
                they tell you which jobs to assess first.
              </div>
            )}

            <div className="pa-tabs" role="tablist" aria-label="PAEI analysis views">
              {TABS.map((t, i) => {
                const count =
                  t.key === "reassignments" ? counts.reassignments
                  : t.key === "redesign" ? counts.redesign
                  : t.key === "pool" ? counts.pool
                  : t.key === "capi" ? counts.capi
                  : null;
                const selected = activeTab === t.key;
                return (
                  <button
                    key={t.key}
                    role="tab"
                    id={`pa-tab-${t.key}`}
                    aria-selected={selected}
                    aria-controls={`pa-panel-${t.key}`}
                    tabIndex={selected ? 0 : -1}
                    ref={(el) => { tabRefs.current[i] = el; }}
                    className={`pa-tab${selected ? " active" : ""}`}
                    onClick={() => setActiveTab(t.key)}
                    onKeyDown={(e) => handleTabKeyDown(e, i)}
                  >
                    {t.label}
                    {count != null && <sup className="pa-count">{count}</sup>}
                  </button>
                );
              })}
            </div>

            <div
              className="pa-tabpanel"
              role="tabpanel"
              id={`pa-panel-${activeTab}`}
              aria-labelledby={`pa-tab-${activeTab}`}
              tabIndex={0}
            >
              {activeTab === "overview" &&
                (analysable && (result.columns || []).length > 0 ? (
                  <PaeiOverviewTab result={result} />
                ) : (
                  <div className="pa-panel">
                    <div className="pa-empty">
                      No comparison can be drawn: {result.reason || "no slot has a profile available to you."}
                    </div>
                  </div>
                ))}
              {/* Independent of `analysable`: CAPI is read off the job records,
                  so it holds even on a run where no slot had a PAEI profile. */}
              {activeTab === "capi" && <PaeiCapiTab result={result} />}
              {activeTab === "reassignments" && <PaeiReassignmentsTab result={result} />}
              {activeTab === "redesign" && <PaeiRedesignTab result={result} />}
              {activeTab === "requirements" && <PaeiRequirementsTab result={result} />}
              {activeTab === "reference" && (
                <PaeiShiftReferenceTab
                  reference={reference}
                  referenceError={referenceError}
                  result={result}
                />
              )}
              {activeTab === "pool" && (
                <PaeiJobPoolTab result={result} coverage={coverage?.pool_coverage || coverage} />
              )}
            </div>
          </>
        )}

        {!result && !running && (
          <section className="pa-card">
            <div className="pa-empty">
              {selectedProcess ?
                "Pick a goal and run the analysis."
              : "Select a process to begin. Its pool coverage is checked before you run anything."}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default CapiPaeiAnalysisPage;
